import { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { ImCancelCircle } from 'react-icons/im';
import { 
  Upload, 
  Button, 
  Alert, 
  Spin, 
  message, 
  Progress, 
  Radio, 
  Typography 
} from 'antd';
import { 
  InboxOutlined, 
  FileExcelOutlined, 
  FileTextOutlined 
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { BeatLoader } from "react-spinners";
import { importMedications } from '../../../services/medications';
import './ImportMedicationsModal.css';

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

const validationSchema = Yup.object().shape({
  file: Yup.mixed().required('Please upload a file')
});

const ImportMedicationsModal = ({ onClose, onImportComplete }) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [importResponse, setImportResponse] = useState(null);
  const [fileType, setFileType] = useState('csv');
  const [importSuccess, setImportSuccess] = useState(false);

  // Auto-refresh data when import is successful
  useEffect(() => {
    if (importSuccess) {
      // Wait a brief moment to ensure backend has processed the data
      const timer = setTimeout(() => {
        onImportComplete();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [importSuccess, onImportComplete]);

  const getAcceptedFileTypes = () => {
    return fileType === 'csv' ? '.csv' : '.xlsx,.xls';
  };

  const handleFileChange = ({ fileList }) => {
    // Limit to only one file
    const newFileList = fileList.slice(-1);
    setFileList(newFileList);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (fileList.length === 0) {
      message.error('Please select a file to upload');
      return;
    }

    const file = fileList[0].originFileObj;
    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      setImportSuccess(false);
      const response = await importMedications(formData);
      console.log('Import response:', response);
      
      // Check various success indicators in the response
      const isSuccess = 
        response?.success === true || 
        response?.data?.success === true ||
        response?.status === 201 ||
        response?.status === 200 ||
        response?.data?.count > 0;
      
      if (isSuccess) {
        // Handle different response structures
        const count = response?.count || response?.data?.count || 0;
        const message = response?.message || response?.data?.message || 'Medications imported successfully';
        const data = response?.data?.data || response?.data || [];
        
        setImportResponse({
          success: true,
          count: count,
          message: message,
          data: data
        });
        
        toast.success(`Successfully imported medications`);
        
        // Set flag for auto-refresh
        setImportSuccess(true);
        
        // Reset form after successful upload
        resetForm();
        setFileList([]);
      } else {
        const errorMessage = 
          response?.error || 
          response?.data?.error || 
          response?.message || 
          response?.data?.message || 
          'Import failed';
          
        setImportResponse({
          success: false,
          message: errorMessage
        });
        
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Import error:', error);
      // Check if the error contains a response with useful data
      const errorResponse = error.response?.data;
      
      // Look for any error messages in the response
      const errorMessage = 
        errorResponse?.error || 
        errorResponse?.message || 
        error.message || 
        'Import failed';
      
      setImportResponse({
        success: false,
        message: errorMessage
      });
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      setSubmitting(false);
    }
  };

  const dummyRequest = ({ onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const handleFinish = () => {
    onImportComplete();
  };

  return (
    <div className="modal-wrapper">
      <div className="modal-container import-modal">
        <div className="modal-header">
          <h2>Import Medications</h2>
          <ImCancelCircle 
            className="close-icon"
            onClick={onClose} 
            size={22} 
          />
        </div>

        <div className="modal-content">
          <Formik
            initialValues={{ file: null }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ handleSubmit, setFieldValue, isSubmitting }) => (
              <Form className="form-container">
                <div className="file-type-selector">
                  <Radio.Group value={fileType} onChange={(e) => setFileType(e.target.value)}>
                    <Radio.Button value="csv">
                      <FileTextOutlined /> CSV File
                    </Radio.Button>
                    <Radio.Button value="excel">
                      <FileExcelOutlined /> Excel File
                    </Radio.Button>
                  </Radio.Group>
                </div>

                <div className="import-instructions">
                  <Alert
                    message="File Format Instructions"
                    description={
                      <ul>
                        <li>Your file should have the following columns: Name, Presentation, Price, Active Molecules and Dosage, Therapeutic Class, Sub-Class, Other Information</li>
                        <li>The file must be in {fileType === 'csv' ? 'CSV' : 'Excel (XLSX/XLS)'} format</li>
                        <li>The first row should be the header row with column names</li>
                        <li>Name and Presentation fields are required for each medication</li>
                        <li>French translations can be added later</li>
                      </ul>
                    }
                    type="info"
                    showIcon
                  />
                </div>

                {!importResponse?.success && (
                  <Dragger
                    name="file"
                    fileList={fileList}
                    onChange={handleFileChange}
                    customRequest={dummyRequest}
                    accept={getAcceptedFileTypes()}
                    beforeUpload={(file) => {
                      setFieldValue('file', file);
                      return false;
                    }}
                    onRemove={() => {
                      setFieldValue('file', null);
                      setFileList([]);
                    }}
                    disabled={uploading}
                    className="medication-file-uploader"
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag {fileType === 'csv' ? 'CSV' : 'Excel'} file to upload
                    </p>
                    <p className="ant-upload-hint">
                      Support for a single {fileType === 'csv' ? '.csv' : '.xlsx or .xls'} file upload
                    </p>
                  </Dragger>
                )}

                {uploading && (
                  <div className="upload-progress">
                    <Spin spinning={true}>
                      <div className="spin-content">
                        <p>Importing medications...</p>
                        <Progress percent={100} status="active" />
                      </div>
                    </Spin>
                  </div>
                )}

                {importResponse && (
                  <div className={`import-result ${importResponse.success ? 'success' : 'error'}`}>
                    <Alert
                      message={importResponse.success ? "Import Successful" : "Import Failed"}
                      description={importResponse.message}
                      type={importResponse.success ? "success" : "error"}
                      showIcon
                    />
                    {importResponse.success && (
                      <div className="imported-count">
                        <Title level={4}>
                          {importResponse.count > 0 
                            ? `${importResponse.count} medications imported` 
                            : "Medications imported successfully"}
                        </Title>
                      </div>
                    )}
                  </div>
                )}

                <div className="button-container">
                  {importResponse?.success ? (
                    <Button
                      type="primary"
                      onClick={handleFinish}
                      className="submit-button full-width"
                    >
                      Close and View Medications
                    </Button>
                  ) : (
                    <>
                      <Button 
                        onClick={onClose} 
                        className="cancel-button"
                        disabled={uploading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={isSubmitting || uploading}
                        disabled={fileList.length === 0 || uploading}
                        className="submit-button"
                      >
                        {uploading ? "Importing..." : "Import Medications"}
                      </Button>
                    </>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <div className="modal-overlay" onClick={onClose} />
    </div>
  );
};

export default ImportMedicationsModal;